import { Link, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { AlertTriangle, ExternalLink } from "lucide-react";

/* Brand-recognizable WhatsApp glyph — lucide-react has no brand icons, so
   this is a hand-authored inline SVG rather than pulling in an icon library
   for one glyph. fill="currentColor" so it colors via the usual text-* classes. */
export const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12.031 0C5.464 0 .132 5.33.132 11.897c0 2.096.548 4.142 1.588 5.945L.032 24l6.304-1.654a11.86 11.86 0 0 0 5.694 1.451h.005c6.567 0 11.898-5.331 11.898-11.898 0-3.18-1.24-6.169-3.487-8.416A11.821 11.821 0 0 0 12.032 0zm0 21.769h-.004a9.85 9.85 0 0 1-5.02-1.375l-.36-.214-3.741.981.999-3.648-.235-.375a9.84 9.84 0 0 1-1.508-5.24c0-5.448 4.434-9.882 9.874-9.882a9.82 9.82 0 0 1 6.988 2.896 9.82 9.82 0 0 1 2.893 6.99c0 5.448-4.434 9.867-9.886 9.867z"/>
  </svg>
);

export const TestBadge = ({ show }) =>
  show ? (
    <Badge data-testid="test-record-badge" className="bg-[hsl(var(--test))]/15 text-[hsl(var(--test))] border-[hsl(var(--test))]/30 border font-mono text-[10px] px-1.5" variant="outline">
      TEST
    </Badge>
  ) : null;

const LIFECYCLE_STYLES = {
  active: "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent",
  on_hold: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  completed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  closed_lost: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  discontinued: "bg-muted text-muted-foreground border",
};

export const LifecycleBadge = ({ status }) => (
  <Badge variant="outline" className={cn("border capitalize", LIFECYCLE_STYLES[status] || "bg-muted")}>
    {(status || "—").replace(/_/g, " ")}
  </Badge>
);

const APPT_STYLES = {
  booked: "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent",
  rescheduled: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30",
  attended: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  no_show: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  cancelled: "bg-muted text-muted-foreground border",
};

export const ApptBadge = ({ status }) => (
  <Badge variant="outline" className={cn("border capitalize", APPT_STYLES[status] || "bg-muted")}>
    {(status || "—").replace(/_/g, " ")}
  </Badge>
);

const INTENT_STYLES = {
  high_intent: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  hot: "bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/20",
  warm: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  cold: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20",
  unclear: "bg-muted text-muted-foreground border",
};

export const IntentBadge = ({ category, source }) =>
  category ? (
    <Badge variant="outline" className={cn("border capitalize", INTENT_STYLES[category])}>
      {category.replace(/_/g, " ")}{source === "ai" ? " (AI)" : ""}
    </Badge>
  ) : null;

const TASK_STYLES = {
  open: "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent",
  completed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  skipped: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  cancelled: "bg-muted text-muted-foreground border",
};

export const TaskBadge = ({ status }) => (
  <Badge variant="outline" className={cn("border capitalize", TASK_STYLES[status] || "bg-muted")}>
    {(status || "—").replace(/_/g, " ")}
  </Badge>
);

export const BookingStateBadge = ({ state }) => {
  const styles = {
    booked: APPT_STYLES.booked,
    human_followup_required: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
    invitation_sent: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20",
    not_invited: "bg-muted text-muted-foreground border",
    cancelled: "bg-muted text-muted-foreground border",
    cancelled_no_consultant: "bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  };
  return (
    <Badge variant="outline" className={cn("border capitalize text-[11px] gap-1", styles[state] || "bg-muted")}>
      {state === "cancelled_no_consultant" && <AlertTriangle className="h-3 w-3" />}
      {state === "cancelled_no_consultant" ? "no consultant assigned" : (state || "—").replace(/_/g, " ")}
    </Badge>
  );
};

/* Canonical AI Voice / automation operational state — one badge, used
   identically on Leads, Calls and Customer 360. The label + owns_outreach
   flag are ALWAYS computed by the backend
   (lead_ownership.derive_voice_operational_state); this component never
   re-derives the rule, it only styles the label it's handed. */
const VOICE_STATE_STYLES = {
  "AI waiting": "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20",
  "AI queued": "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20",
  "AI calling": "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent",
  "AI retry scheduled": "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  "Human follow-up required": "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  "AI completed": "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  "AI not applicable": "bg-muted text-muted-foreground border",
};

export const voiceOwnsOutreach = (vs) => !!(vs && vs.owns_outreach);

export const VoiceStateBadge = ({ vs, showOwnership = false, className }) => {
  if (!vs || !vs.label) return null;
  const owns = showOwnership && vs.owns_outreach;
  return (
    <Badge data-testid="voice-state-badge" variant="outline"
      className={cn("border text-[11px] gap-1", VOICE_STATE_STYLES[vs.label] || "bg-muted", className)}>
      {vs.label === "Human follow-up required" && <AlertTriangle className="h-3 w-3" />}
      {vs.label}{owns ? " — AI owns outreach" : ""}
    </Badge>
  );
};

/* Customers-page "Current State" — an operational summary derived
   backend-side (operational_state.compute_current_state) from canonical
   Opportunity/Voice/appointment/task/payment/process truth. This component
   only styles the label it's handed; it never re-derives the rule. The AI
   portion of the label is always the exact canonical Voice label plus
   " — AI owns outreach", so its color intentionally matches
   VOICE_STATE_STYLES above rather than inventing a second palette. */
const CURRENT_STATE_STYLES = [
  [/^Completed$/, "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"],
  [/^Lost \/ Closed$/, "bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/20"],
  [/^On hold$/, "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30"],
  [/outcome required$/, "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30"],
  [/^Consultation booked$/, "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"],
  [/^AI (queued|waiting) —/, "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20"],
  [/^AI calling —/, "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent"],
  [/^AI retry scheduled —/, "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30"],
  [/^Human follow-up required$/, "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30"],
  [/^Payment pending$/, "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30"],
  [/^Process \/ handover pending$/, "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20"],
  [/^In process$/, "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20"],
  [/^Follow-up in progress$/, "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20"],
];

export const CurrentStateBadge = ({ state, className }) => {
  if (!state) return <span className="text-xs text-muted-foreground">—</span>;
  const match = CURRENT_STATE_STYLES.find(([re]) => re.test(state));
  return (
    <Badge data-testid="current-state-badge" variant="outline"
      className={cn("border text-[11px] gap-1 whitespace-normal text-left", match ? match[1] : "bg-muted", className)}>
      {/outcome required$|^Human follow-up required$/.test(state) && <AlertTriangle className="h-3 w-3 shrink-0" />}
      {state}
    </Badge>
  );
};

/* Customers-page "Next Action" — reads operational_state.compute_next_action's
   {type, due_at, owner, urgency} exactly as returned; never re-derives due
   dates or guesses an owner on the frontend. */
const NEXT_ACTION_URGENCY_STYLES = {
  overdue: "text-[hsl(var(--danger))] font-medium",
  today: "text-[hsl(var(--warning))] font-medium",
  future: "text-muted-foreground",
  none: "text-muted-foreground italic",
};

export const NextActionCell = ({ action, fmtIST }) => {
  if (!action || !action.type) {
    return <span className={NEXT_ACTION_URGENCY_STYLES.none}>No action scheduled</span>;
  }
  const when = action.due_at ? fmtIST(action.due_at) : "Due now";
  return (
    <div className={cn("text-xs leading-tight", NEXT_ACTION_URGENCY_STYLES[action.urgency] || "text-muted-foreground")}>
      <div className="font-medium">{action.type}</div>
      <div>{when}{action.owner ? ` — ${action.owner}` : ""}</div>
    </div>
  );
};

export const EmptyState = ({ icon: Icon, title, hint, action, testId }) => (
  <div data-testid={testId || "empty-state"} className="flex flex-col items-center justify-center py-12 text-center">
    {Icon && <Icon className="h-8 w-8 text-muted-foreground/50 mb-3" />}
    <p className="text-sm font-medium">{title}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1 max-w-sm">{hint}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

/* 2026-09 R7 — the shared "the request failed" state: never a blank page or an
   endless skeleton. Always offers Retry when the caller can retry. `message`
   is the (already human) error detail; `title` names what failed. */
export const ErrorState = ({ title = "Couldn't load this", message, onRetry, testId, className }) => (
  <div role="alert" data-testid={testId || "error-state"}
    className={cn("flex flex-col items-center justify-center py-12 px-4 text-center", className)}>
    <AlertTriangle className="h-8 w-8 text-destructive/70 mb-3" />
    <p className="text-sm font-medium">{title}</p>
    {message && <p className="text-xs text-muted-foreground mt-1 max-w-sm break-words">{message}</p>}
    {onRetry && (
      <Button data-testid={testId ? `${testId}-retry` : "error-state-retry"} variant="outline" size="sm"
        className="mt-4" onClick={onRetry}>Retry</Button>
    )}
  </div>
);

/* Shared list/table loading placeholder — `rows` stacked skeleton bars. */
export const ListSkeleton = ({ rows = 6, className, testId }) => (
  <div data-testid={testId || "list-skeleton"} aria-busy="true" aria-label="Loading"
    className={cn("space-y-2 p-3", className)}>
    {Array.from({ length: rows }, (_, i) => <Skeleton key={i} className="h-10 w-full" />)}
  </div>
);

export const CustomerId = ({ id }) => (
  <span className="font-mono text-xs text-muted-foreground">{id}</span>
);

// ---------------------------------------------------------------------------
// Evidence citations (P5 acceptance-review correction) — renders a backend-
// resolved {text, customer_360_url} citation list. The frontend NEVER
// constructs a Customer 360 URL itself: `customer_360_url` is either a real,
// backend-authorized path or `null` — when `null`, only the plain citation
// text is shown, with no link, no customer_id, no discoverable path of any
// kind. When present, the link carries `return_to` via the exact same
// pattern every other Customer-360-linking surface in this app already
// uses, so the existing Back control on Customer 360 works unchanged.
// ---------------------------------------------------------------------------
export const CitationList = ({ citations, emptyHint, testId }) => {
  const location = useLocation();
  const returnTo = encodeURIComponent(location.pathname + location.search);
  if (!citations || citations.length === 0) {
    return emptyHint ? <p className="text-xs text-muted-foreground">{emptyHint}</p> : null;
  }
  return (
    <ul className="space-y-1" data-testid={testId || "citation-list"}>
      {citations.map((c, i) => (
        <li key={i} className="text-xs flex items-center gap-1.5" data-testid="citation-row">
          {c.customer_360_url ? (
            <Link to={`${c.customer_360_url}?return_to=${returnTo}`}
              className="text-primary hover:underline inline-flex items-center gap-1"
              data-testid="citation-link">
              {c.text}<ExternalLink className="h-3 w-3 shrink-0" />
            </Link>
          ) : (
            <span className="text-muted-foreground" data-testid="citation-text-only">{c.text}</span>
          )}
        </li>
      ))}
    </ul>
  );
};

/* Communication (integration outbox) delivery states — "Sent" vs "Pending
   provider" etc. must be obvious wherever automated comms are shown. */
const COMM_STYLES = {
  sent: { label: "Sent", cls: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30" },
  pending: { label: "Queued", cls: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20" },
  processing: { label: "Sending…", cls: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20" },
  skipped_provider_off: { label: "Pending provider", cls: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30" },
  skipped_test: { label: "Test — not sent", cls: "bg-[hsl(var(--test))]/15 text-[hsl(var(--test))] border-[hsl(var(--test))]/30" },
  failed: { label: "Failed", cls: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30" },
};

export const CommStatusBadge = ({ status }) => {
  const s = COMM_STYLES[status] || { label: status || "—", cls: "bg-muted text-muted-foreground border" };
  return (
    <Badge data-testid="comm-status-badge" variant="outline" className={cn("border text-[11px]", s.cls)}>
      {s.label}
    </Badge>
  );
};

/* Google Calendar sync state on appointments (canonical record is SalesOS). */
const CAL_STYLES = {
  synced: { label: "Calendar synced", cls: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30" },
  pending: { label: "Sync pending", cls: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20" },
  failed: { label: "Sync failed", cls: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30" },
  not_connected: { label: "Calendar off", cls: "bg-muted text-muted-foreground border" },
  cancelled_synced: { label: "Event removed", cls: "bg-muted text-muted-foreground border" },
};

export const CalSyncBadge = ({ status }) => {
  if (!status) return null;
  const s = CAL_STYLES[status] || { label: status.replace(/_/g, " "), cls: "bg-muted text-muted-foreground border" };
  return (
    <Badge data-testid="cal-sync-badge" variant="outline" className={cn("border text-[10px]", s.cls)}>
      {s.label}
    </Badge>
  );
};

/* Human labels for outbox rows (never expose payload contents). */
const KIND_LABELS = {
  booking_invitation: "Booking invitation",
  booking_confirmation: "Booking confirmation",
  reminder_prev_day: "Reminder — previous day",
  reminder_same_day: "Reminder — same day",
  reminder_t60: "Reminder — 60 min before",
  reschedule_confirmation: "Reschedule confirmation",
  cancellation_confirmation: "Cancellation confirmation",
};

export const commLabel = (item) => {
  if (item.kind && KIND_LABELS[item.kind]) return `WhatsApp: ${KIND_LABELS[item.kind]}`;
  const map = {
    upsert_event: "Google Calendar event sync",
    delete_event: "Google Calendar event removal",
    outbound_call: "Voice agent call",
    send_event: "Meta conversion event",
    send_template: "WhatsApp message",
  };
  if (item.action === "send_event" && item.meta?.event_name) {
    return `Meta conversion event — ${item.meta.event_name}`;
  }
  return map[item.action] || `${item.provider} · ${item.action}`;
};

/* One-line, readable detail for a Meta conversion event's timeline entry
   (2026-09 round 7): triggering rule, dataset, TEST/live, dispatch state and
   correlation id. Older rows without a `meta` block return an empty list. */
export const commMetaDetails = (item) => {
  const m = item?.meta;
  if (!m) return [];
  const rule = m.trigger_rule;
  const parts = [];
  if (rule?.name) parts.push(`Rule: ${rule.name}${rule.kind === "automation" ? " (automation)" : ""}`);
  else if (rule?.id) parts.push("Rule: (since removed)");
  if (m.dataset?.name || m.dataset?.key) parts.push(`Dataset: ${m.dataset.name || m.dataset.key}`);
  if (m.environment) parts.push(m.environment === "TEST" ? "TEST" : "LIVE");
  if (m.dispatch?.label) parts.push(m.dispatch.label);
  if (m.dispatch?.provider_result) parts.push(m.dispatch.provider_result);
  return parts;
};
